import * as clack from '@clack/prompts'
import { execFileSync } from 'node:child_process'
import fs from 'node:fs'
import path from 'node:path'
import type { ResolvedBetterstartConfig } from '@cli-types/core-engine/config.js'
import { captureFile, restoreFile } from '@cli-utils/files.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace/resolve-admin-namespace.js'
import { createAdminShadcnComponentsJson } from './create-admin-shadcn-components-json.js'
import { getHostProjectFilesToRestore } from './get-host-project-files-to-restore.js'
import { resolveLocalShadcnBin } from './resolve-local-shadcn-bin.js'
export function runShadcnPresetUpdate({
  cwd,
  config,
  preset,
  only
}: {
  cwd: string
  config: ResolvedBetterstartConfig
  preset: string
  only?: string
}): void {
  const namespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)
  const adminGlobalsPath = path.join(cwd, config.paths.admin, namespace.globalsFile)
  const componentsJsonPath = path.join(cwd, 'components.json')
  const shadcnBackupPath = `${componentsJsonPath}.bak`
  const restoreAfterApplyPaths = [
    componentsJsonPath,
    shadcnBackupPath,
    path.join(cwd, config.paths.admin, 'lib', 'utils.ts'),
    ...getHostProjectFilesToRestore(cwd)
  ]

  if (!preset) {
    clack.cancel('--shadcn-preset requires a preset code, preset name, or preset URL.')
    process.exit(1)
  }

  if (!fs.existsSync(adminGlobalsPath)) {
    clack.cancel(
      `Admin globals file not found at ${path.relative(cwd, adminGlobalsPath)}. Run 'betterstart update admin-globals' first.`
    )
    process.exit(1)
  }

  const shadcnBin = resolveLocalShadcnBin(cwd)
  const restoreSnapshots = restoreAfterApplyPaths.map((filePath) => ({
    filePath,
    snapshot: captureFile(filePath)
  }))

  clack.intro('BetterStart Shadcn Preset')
  clack.log.info(`Applying preset to ${path.join(config.paths.admin, 'components/ui')}`)

  let failed = false
  try {
    fs.writeFileSync(
      componentsJsonPath,
      `${JSON.stringify(createAdminShadcnComponentsJson(config), null, 2)}\n`,
      'utf-8'
    )

    const args = ['apply', '--preset', preset, '--cwd', cwd, '--yes']
    if (only) {
      args.push('--only', only)
    }

    execFileSync(shadcnBin, args, { cwd, stdio: 'inherit' })
  } catch {
    failed = true
  } finally {
    for (const { filePath, snapshot } of restoreSnapshots.reverse()) {
      restoreFile(filePath, snapshot)
    }
  }

  if (failed) {
    clack.cancel('shadcn preset application failed.')
    process.exit(1)
  }

  clack.outro(
    only
      ? `Applied shadcn preset parts (${only}) to the Admin.`
      : 'Applied shadcn preset to Admin UI components and styles.'
  )
}
